import React, { useState } from "react";
import styled from "styled-components";
import Colors from "../../../styles/Colors";
import { H1, SmallRegular } from "../../../styles/Fonts";
import RewardItem from "./RewardItem";
import { RewardProps } from "../index";
import { claimReferralRewards } from "../../../utils/api/referralApi";
import { useWeb3React } from "@web3-react/core";
import Button from "../../../components/Button";

const Rewards: React.FC<{
  rewards: RewardProps[];
  updateRewardRecords: () => void;
}> = (props) => {
  const { rewards, updateRewardRecords } = props;

  const { account, chainId, library } = useWeb3React();

  const [selectedWeeks, setSelectedWeeks] = useState<{
    [key: string]: RewardProps;
  }>({});
  const [isClaiming, setIsClaiming] = useState<boolean>(false);

  const handleChange = (reward: RewardProps) => {
    const copy = { ...selectedWeeks };
    if (!selectedWeeks[reward.ID]) {
      setSelectedWeeks({ ...copy, [reward.ID]: reward });
    } else {
      delete copy[reward.ID];
      setSelectedWeeks(copy);
    }
  };

  const handleClaimReward = async () => {
    if (account && chainId && library) {
      const claimWeeks = Object.values(selectedWeeks);
      const parsedClaims = claimWeeks.map((w) => {
        const weekNum = w.RewardSession.WeekNumber;
        const claimAmount = +w.Discount + +w.Rebate;
        const proof = w.MerkleProof;
        return [weekNum, claimAmount, proof];
      });

      setIsClaiming(true);

      try {
        await claimReferralRewards(account, chainId, library, parsedClaims);
      } catch (e) {}

      setIsClaiming(false);
      setSelectedWeeks({});
      updateRewardRecords();
    }
  };

  return (
    <Container>
      <RewardsContainer>
        <Subtitle>Rewards</Subtitle>
        <SectionContainer>
          <BtnContainer>
            <Button
              onClick={handleClaimReward}
              btnText="Claim Rewards"
              btnType="primary"
              disabled={isClaiming || Object.keys(selectedWeeks).length === 0}
              isLoading={isClaiming}
            />
          </BtnContainer>
          <TierHeaderRow>
            <HeaderText></HeaderText>
            <HeaderText>Week No.</HeaderText>
            <HeaderText>Rewards</HeaderText>
            <HeaderText>Status</HeaderText>
          </TierHeaderRow>
          <BackgroundContainer>
            {rewards.length > 0 ? (
              rewards.map((reward) => (
                <RewardItem
                  key={reward.ID}
                  reward={reward}
                  handleChange={handleChange}
                  isSelected={!!selectedWeeks[reward.ID]}
                />
              ))
            ) : (
              <NoRecordContainer>
                <NoRecord>No Record</NoRecord>
              </NoRecordContainer>
            )}
          </BackgroundContainer>
        </SectionContainer>
      </RewardsContainer>
    </Container>
  );
};

export default Rewards;

const Container = styled.div``;

const RewardsContainer = styled.div`
  margin-top: 60px;
`;

const Subtitle = styled(H1)`
  font-size: 24px;
  line-height: 32px;
  text-align: left;
  color: ${Colors.ivory};
  font-family: TT Firs Neue, Goldman-Sans, Helvetica Neue, Helvetica, Roboto,
    PingFang TC, 微軟雅黑, Microsoft Yahei, sans-serif;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    font-size: 20px;
    line-height: 26px;
  }
  @media (max-width: 576px) {
    font-size: 18px;
    line-height: 24px;
  }
`;

const TierRow = styled.div`
  display: grid;
  grid-template-columns: 40px 200px 200px 1fr;
  align-items: center;
  padding: 0 20px;
  @media (max-width: 768px) {
    grid-template-columns: 30px repeat(3, 1fr);
  }
`;

const TierHeaderRow = styled(TierRow)`
  padding: 0px 20px 12px 20px;
  margin-top: 12px;
  div:last-child {
    text-align: right;
  }
`;

const BackgroundContainer = styled.div`
  div:not(:first-child) {
    margin-top: 20px;
  }
`;

const HeaderText = styled(SmallRegular)`
  color: ${Colors.gray4};
  @media (max-width: 768px) {
    font-size: 13px;
    line-height: 22px;
  }
`;

const SectionContainer = styled.div`
  background-color: ${Colors.backgroundGray2};
  border-radius: 8px;
  padding: 24px;
`;

const BtnContainer = styled.div`
  width: 160px;
  margin-left: auto;
`;

const NoRecordContainer = styled.div`
  text-align: center;
  padding: 60px 0;
`;

const NoRecord = styled.span`
  display: inline-block;
  padding: 4px 20px;
  border-radius: 8px;
  background-color: ${Colors.backgroundGray4};
  margin: auto;
  font-size: 14px;
  line-height: 22px;
  color: ${Colors.ivory};
`;
